import type { ComponentProps, ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * The Figma `Radio_custom` card: a 138x138 tile with a radio dot top-left, a
 * large glyph in the middle and a label at the bottom. Selection is carried by
 * a brand border and a soft blue glow, not by the dot alone.
 *
 * A real radio input sits underneath, visually hidden. A div with
 * `role="radio"` would have to re-implement arrow-key roving and form
 * participation, and would still be worse for anyone using a screen reader.
 *
 * Descendants are styled through `group-has-[:checked]` rather than `peer-*`:
 * the peer modifier compiles to a sibling combinator, which does not reach
 * inside a sibling, and the dot lives inside one.
 */
export type ChoiceCardProps = Omit<ComponentProps<'input'>, 'type' | 'children'> & {
  label: string
  icon: ReactNode
}

export function ChoiceCard({ label, icon, className, ...props }: ChoiceCardProps) {
  return (
    <label
      className={cn(
        'group relative flex size-[138px] cursor-pointer flex-col justify-between rounded-md p-3',
        // Gives under the finger and settles into the selected ring, so
        // choosing feels like pressing something rather than toggling a class.
        'bg-slate-100 transition-[box-shadow,transform,background-color]',
        'duration-[--duration-fast] ease-[--ease-out-soft] active:scale-[0.97]',
        'hover:bg-slate-200/60',
        'has-[:checked]:ring-brand-600 has-[:checked]:ring-1',
        'has-[:checked]:shadow-[0_0_8px_rgba(37,99,235,0.26)]',
        'has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2',
        className,
      )}
    >
      <input type="radio" className="sr-only" {...props} />

      <span
        aria-hidden="true"
        className={cn(
          'flex size-[18px] items-center justify-center rounded-full',
          'border border-slate-400 bg-slate-50',
          'group-has-[:checked]:border-brand-600 group-has-[:checked]:bg-white',
        )}
      >
        <span className="bg-brand-600 group-has-[:checked]:animate-pop hidden size-2.5 rounded-full group-has-[:checked]:block" />
      </span>

      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-0 flex items-center justify-center',
          'group-has-[:checked]:text-brand-600 text-slate-400',
          '[&_svg]:size-14',
        )}
      >
        {icon}
      </span>

      <span className="group-has-[:checked]:text-brand-600 text-xs font-medium text-slate-400">
        {label}
      </span>
    </label>
  )
}
